import React, { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  Layers,
  Braces,
  Hash,
  Type,
  ToggleLeft,
  List,
} from "lucide-react";
import { AvailableVariable } from "../ui/types";

interface InputSchemaViewProps {
  availableVariables: AvailableVariable[];
  onDragStart: (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output",
  ) => void;
}

// ── Type icon + color mapping ───────────────────────────────────────────────
const typeConfig: Record<
  string,
  { icon: React.ReactNode; bg: string; text: string; border: string }
> = {
  string: {
    icon: <Type className="w-2.5 h-2.5" />,
    bg: "bg-emerald-50",
    text: "text-emerald-600",
    border: "border-emerald-200",
  },
  number: {
    icon: <Hash className="w-2.5 h-2.5" />,
    bg: "bg-amber-50",
    text: "text-amber-600",
    border: "border-amber-200",
  },
  boolean: {
    icon: <ToggleLeft className="w-2.5 h-2.5" />,
    bg: "bg-violet-50",
    text: "text-violet-600",
    border: "border-violet-200",
  },
  object: {
    icon: <Braces className="w-2.5 h-2.5" />,
    bg: "bg-blue-50",
    text: "text-blue-600",
    border: "border-blue-200",
  },
  array: {
    icon: <Layers className="w-2.5 h-2.5" />,
    bg: "bg-purple-50",
    text: "text-purple-600",
    border: "border-purple-200",
  },
  null: {
    icon: <span className="text-[8px] font-bold">N</span>,
    bg: "bg-gray-50",
    text: "text-gray-400",
    border: "border-gray-200",
  },
};

function getType(value: any): { label: string; key: string } {
  if (value === null || value === undefined) return { label: "null", key: "null" };
  if (Array.isArray(value)) return { label: `[${value.length}]`, key: "array" };
  if (typeof value === "object") return { label: "{}", key: "object" };
  return { label: typeof value, key: typeof value };
}

// ── Recursive schema row ────────────────────────────────────────────────────
const SchemaRow: React.FC<{
  name: string;
  value: any;
  path: string;
  nodeName: string;
  depth: number;
  onDragStart: InputSchemaViewProps["onDragStart"];
}> = ({ name, value, path, nodeName, depth, onDragStart }) => {
  const [open, setOpen] = useState(depth < 1);
  const { label, key } = getType(value);
  const config = typeConfig[key] || typeConfig.null;

  const isExpandable =
    (key === "object" && value && Object.keys(value).length > 0) ||
    (key === "array" && Array.isArray(value) && value.length > 0);

  const children: [string, any][] = (() => {
    if (!isExpandable) return [];
    if (key === "array") {
      const first = value[0];
      if (first && typeof first === "object" && !Array.isArray(first)) {
        return Object.entries(first);
      }
      return value.map((item: any, i: number) => [`${i}`, item]);
    }
    return Object.entries(value);
  })();

  const childPath = (childKey: string) => {
    if (key === "array") {
      const first = value[0];
      if (first && typeof first === "object" && !Array.isArray(first)) {
        return `${path}[0].${childKey}`;
      }
      return `${path}[${childKey}]`;
    }
    return `${path}.${childKey}`;
  };

  const handleDrag = (e: React.DragEvent) => {
    e.stopPropagation();
    onDragStart(e, path, nodeName, "output");
  };

  return (
    <>
      <div
        className="flex items-center gap-1 py-[5px] hover:bg-indigo-50/60 rounded-sm transition-colors group"
        style={{ paddingLeft: `${12 + depth * 16}px`, paddingRight: 8 }}
      >
        {/* Expand toggle */}
        {isExpandable ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
            className="w-4 h-4 flex items-center justify-center shrink-0 rounded hover:bg-gray-200/70 transition-colors"
          >
            {open ? (
              <ChevronDown className="w-3 h-3 text-gray-400" />
            ) : (
              <ChevronRight className="w-3 h-3 text-gray-400" />
            )}
          </button>
        ) : (
          <div className="w-4 h-4 shrink-0" />
        )}

        {/* Draggable pill */}
        <div
          draggable
          onDragStart={handleDrag}
          className={`flex items-center gap-1.5 flex-1 min-w-0 px-1.5 py-0.5 rounded cursor-grab active:cursor-grabbing border border-transparent hover:border-indigo-200 hover:bg-white hover:shadow-sm transition-all`}
        >
          <GripVertical className="w-3 h-3 text-gray-300 group-hover:text-indigo-400 shrink-0 transition-colors" />

          {/* Type icon */}
          <span
            className={`w-4 h-4 flex items-center justify-center rounded ${config.bg} ${config.text} shrink-0`}
          >
            {config.icon}
          </span>

          {/* Field name */}
          <span className="text-[11px] font-medium text-gray-800 truncate flex-1">
            {name}
          </span>

          {/* Type label */}
          <span
            className={`text-[9px] font-semibold uppercase px-1 py-px rounded ${config.bg} ${config.text} shrink-0`}
          >
            {label}
          </span>
        </div>
      </div>

      {/* Children */}
      {isExpandable && open && (
        <div className="relative">
          {/* Tree connector line */}
          <div
            className="absolute top-0 bottom-2 border-l border-gray-200"
            style={{ left: `${20 + depth * 16}px` }}
          />
          {children.map(([childKey, childVal]) => (
            <SchemaRow
              key={childKey}
              name={childKey}
              value={childVal}
              path={childPath(childKey)}
              nodeName={nodeName}
              depth={depth + 1}
              onDragStart={onDragStart}
            />
          ))}
        </div>
      )}
    </>
  );
};

// ── Section group (Outputs / Inputs collapsible header) ─────────────────────
const sectionColors = {
  emerald: { dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50" },
  blue: { dot: "bg-blue-500", text: "text-blue-700", bg: "bg-blue-50" },
};

const SectionGroup: React.FC<{
  label: string;
  color: "emerald" | "blue";
  nodeName: string;
  type: "input" | "output";
  onDragStart: InputSchemaViewProps["onDragStart"];
  children: React.ReactNode;
}> = ({ label, color, nodeName, type, onDragStart, children }) => {
  const [open, setOpen] = useState(true);
  const c = sectionColors[color];

  return (
    <div>
      <div className={`flex items-center gap-1 px-3 py-1.5 hover:bg-gray-50 transition-colors`}>
        <button
          onClick={() => setOpen(!open)}
          className="w-3 h-3 flex items-center justify-center shrink-0"
        >
          {open ? (
            <ChevronDown className="w-2.5 h-2.5 text-gray-400" />
          ) : (
            <ChevronRight className="w-2.5 h-2.5 text-gray-400" />
          )}
        </button>
        <div
          draggable
          onDragStart={(e) => {
            e.stopPropagation();
            onDragStart(e, "", nodeName, type);
          }}
          className="flex items-center gap-1.5 flex-1 cursor-grab active:cursor-grabbing px-1 py-0.5 rounded hover:bg-indigo-50 transition-colors"
        >
          <GripVertical className="w-3 h-3 text-gray-300 shrink-0" />
          <div className={`w-1.5 h-1.5 rounded-full ${c.dot} shrink-0`} />
          <span className={`text-[10px] font-bold uppercase tracking-wider ${c.text}`}>
            {label}
          </span>
        </div>
      </div>
      {open && <div>{children}</div>}
    </div>
  );
};

// ── Node accordion ──────────────────────────────────────────────────────────
const NodeAccordion: React.FC<{
  nodeData: AvailableVariable;
  defaultOpen: boolean;
  onDragStart: InputSchemaViewProps["onDragStart"];
}> = ({ nodeData, defaultOpen, onDragStart }) => {
  const [open, setOpen] = useState(defaultOpen);
  const hasOutputs =
    nodeData.outputs && Object.keys(nodeData.outputs).length > 0;
  const hasInputs =
    nodeData.inputs && Object.keys(nodeData.inputs).length > 0;
  const totalFields =
    (hasOutputs ? Object.keys(nodeData.outputs).length : 0) +
    (hasInputs ? Object.keys(nodeData.inputs).length : 0);

  if (!hasOutputs && !hasInputs) return null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 transition-colors"
      >
        <span className="w-4 h-4 flex items-center justify-center">
          {open ? (
            <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
          )}
        </span>
        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
        <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wide truncate flex-1 text-left">
          {nodeData.nodeName}
        </span>
        <span className="text-[9px] text-gray-400 tabular-nums shrink-0">
          {totalFields}
        </span>
      </button>

      {/* Body */}
      {open && (
        <div className="border-t border-gray-100 py-1">
          {hasOutputs && (
            <SectionGroup label="Outputs" color="emerald" nodeName={nodeData.nodeName} type="output" onDragStart={onDragStart}>
              {Object.entries(nodeData.outputs).map(([key, value]) => (
                <SchemaRow
                  key={`o-${key}`}
                  name={key}
                  value={value}
                  path={key}
                  nodeName={nodeData.nodeName}
                  depth={0}
                  onDragStart={onDragStart}
                />
              ))}
            </SectionGroup>
          )}
          {hasInputs && (
            <SectionGroup label="Inputs" color="blue" nodeName={nodeData.nodeName} type="input" onDragStart={onDragStart}>
              {Object.entries(nodeData.inputs).map(([key, value]) => (
                <SchemaRow
                  key={`i-${key}`}
                  name={key}
                  value={value}
                  path={key}
                  nodeName={nodeData.nodeName}
                  depth={0}
                  onDragStart={(e, variable, name, _type) =>
                    onDragStart(e, variable, name, "input")
                  }
                />
              ))}
            </SectionGroup>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main component ──────────────────────────────────────────────────────────
export const InputSchemaView: React.FC<InputSchemaViewProps> = ({
  availableVariables,
  onDragStart,
}) => {
  if (availableVariables.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <List className="w-8 h-8 text-gray-300 mb-2" />
        <p className="text-xs text-gray-500 font-medium">No connected nodes</p>
        <p className="text-xs text-gray-400 mt-1">
          Connect nodes to see their schema
        </p>
      </div>
    );
  }

  return (
    <div className="p-2 space-y-1.5">
      {availableVariables.map((nodeData, index) => (
        <NodeAccordion
          key={nodeData.nodeId}
          nodeData={nodeData}
          defaultOpen={index === availableVariables.length - 1}
          onDragStart={onDragStart}
        />
      ))}
    </div>
  );
};

export default InputSchemaView;
